// Simple animation utility to replace anime.js
export type AnimationOptions = {
  duration?: number
  easing?: string
  delay?: number
  onComplete?: () => void
}

export function animate(element: HTMLElement, properties: Record<string, any>, options: AnimationOptions = {}) {
  const { duration = 300, easing = "ease", delay = 0, onComplete } = options

  // Set transition properties
  element.style.transition = `all ${duration}ms ${easing} ${delay}ms`

  // Apply the styles
  Object.entries(properties).forEach(([key, value]) => {
    if (key === "opacity") {
      element.style.opacity = String(value)
    } else if (key === "translateX") {
      element.style.transform = `translateX(${value}px)`
    } else if (key === "translateY") {
      element.style.transform = `translateY(${value}px)`
    }
  })

  // Handle completion
  if (onComplete) {
    const transitionEndHandler = () => {
      element.removeEventListener("transitionend", transitionEndHandler)
      onComplete()
    }
    element.addEventListener("transitionend", transitionEndHandler)
  }
}

export function animateChildren(
  parentElement: HTMLElement,
  properties: Record<string, any>,
  options: AnimationOptions = {},
) {
  const children = Array.from(parentElement.children) as HTMLElement[]

  children.forEach((child, index) => {
    animate(child, properties, {
      ...options,
      delay: (options.delay || 0) + index * 50, // Stagger effect
    })
  })
}

export function fadeIn(element: HTMLElement, options: AnimationOptions = {}) {
  // Set initial state
  element.style.opacity = "0"

  // Trigger animation on next frame
  requestAnimationFrame(() => {
    animate(element, { opacity: 1 }, options)
  })
}

export function slideIn(
  element: HTMLElement,
  direction: "left" | "right" | "up" | "down" = "up",
  distance = 20,
  options: AnimationOptions = {},
) {
  // Set initial state
  element.style.opacity = "0"

  let initialTransform = ""
  switch (direction) {
    case "left":
      initialTransform = `translateX(${distance}px)`
      break
    case "right":
      initialTransform = `translateX(-${distance}px)`
      break
    case "up":
      initialTransform = `translateY(${distance}px)`
      break
    case "down":
      initialTransform = `translateY(-${distance}px)`
      break
  }

  element.style.transform = initialTransform

  // Trigger animation on next frame
  requestAnimationFrame(() => {
    animate(
      element,
      {
        opacity: 1,
        [direction === "left" || direction === "right" ? "translateX" : "translateY"]: 0,
      },
      options,
    )
  })
}
